import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarPlus, ChevronLeft, ChevronRight, Lock, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/StateBlocks";
import { NewEventDialog } from "@/components/run/NewEventDialog";
import { runService } from "@/services/runService";
import { useCrmActions } from "@/context/CrmActionsContext";
import { CALENDAR_VIEWS, eventTypeLabel } from "@/config/runOptions";
import { fmtTime, fmtDate, fmtWeekday, fmtDateTime } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const startOfWeek = (d) => addDays(startOfDay(d), -new Date(d).getDay());
const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();
const isToday = (d) => sameDay(d, new Date());
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const typeColor = (e) => {
  if (e.source === "system") return e.system_type === "followup" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-brand-navy/10 text-brand-navy border-brand-navy/20";
  return { closing: "bg-emerald-100 text-emerald-800 border-emerald-200", showing: "bg-blue-100 text-blue-800 border-blue-200",
    open_house: "bg-purple-100 text-purple-800 border-purple-200", deadline: "bg-red-100 text-red-800 border-red-200",
    compliance: "bg-orange-100 text-orange-800 border-orange-200" }[e.event_type] || "bg-brand-gold/15 text-foreground border-brand-gold/30";
};

export default function Calendar() {
  const navigate = useNavigate();
  const { openNewEvent } = useCrmActions();
  const [view, setView] = useState(window.innerWidth < 768 ? "agenda" : "month");
  const [anchor, setAnchor] = useState(startOfDay(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editEvent, setEditEvent] = useState(null);

  const range = useMemo(() => {
    if (view === "month") {
      const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const gridStart = startOfWeek(first);
      return { start: gridStart, end: addDays(gridStart, 42) };
    }
    if (view === "week") { const s = startOfWeek(anchor); return { start: s, end: addDays(s, 7) }; }
    if (view === "day") return { start: startOfDay(anchor), end: addDays(startOfDay(anchor), 1) };
    return { start: startOfDay(new Date()), end: addDays(startOfDay(new Date()), 30) }; // agenda
  }, [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { events } = await runService.listEvents({ start: range.start.toISOString(), end: range.end.toISOString() });
      setEvents(events);
    } catch { toast.error("Could not load calendar"); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm:changed", h);
    return () => window.removeEventListener("crm:changed", h);
  }, [load]);

  const eventsForDay = (day) => events.filter((e) => sameDay(e.start_at, day)).sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));

  const go = (dir) => {
    if (view === "month") setAnchor(addMonths(anchor, dir));
    else if (view === "week") setAnchor(addDays(anchor, dir * 7));
    else if (view === "day") setAnchor(addDays(anchor, dir));
    else setAnchor(startOfDay(new Date()));
  };

  const headerLabel = view === "month" ? anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : view === "week" ? `Week of ${fmtDate(startOfWeek(anchor))}`
    : view === "day" ? fmtWeekday(anchor)
    : "Next 30 days";

  const cancelSelected = async () => {
    try { await runService.cancelEvent(selected.id); toast.success("Event cancelled"); setSelected(null); load(); }
    catch (e) { toast.error(e.message || "Could not cancel"); }
  };

  const EventChip = ({ e }) => (
    <button onClick={() => setSelected(e)} data-testid={`cal-event-${e.id}`}
      className={`flex w-full items-center gap-1 truncate rounded border px-1.5 py-0.5 text-left text-[11px] ${typeColor(e)} ${e.source === "system" ? "border-dashed" : ""}`}>
      {e.source === "system" && <Lock className="h-2.5 w-2.5 shrink-0" />}
      {!e.all_day && <span className="shrink-0 font-medium">{fmtTime(e.start_at)}</span>}
      <span className="truncate">{e.title}</span>
    </button>
  );

  return (
    <AppShell>
      <div className="space-y-6" data-testid="calendar-page">
        <PageHeader eyebrow="AgentHQ · Run" title="Calendar"
          description="Your appointments, showings, closings and deadlines — closing dates and follow-ups appear automatically."
          actions={<Button onClick={() => openNewEvent()} data-testid="event-new"><CalendarPlus className="h-4 w-4" /> New Event</Button>} />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => go(-1)} data-testid="cal-prev" aria-label="Previous"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setAnchor(startOfDay(new Date()))} data-testid="cal-today">Today</Button>
            <Button variant="outline" size="sm" onClick={() => go(1)} data-testid="cal-next" aria-label="Next"><ChevronRight className="h-4 w-4" /></Button>
            <span className="ml-2 font-heading text-lg font-semibold text-foreground" data-testid="cal-range-label">{headerLabel}</span>
          </div>
          <div className="flex gap-1 rounded-lg border border-border p-0.5" data-testid="cal-view-switch">
            {CALENDAR_VIEWS.map((v) => (
              <button key={v.key} onClick={() => setView(v.key)} data-testid={`cal-view-${v.key}`}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === v.key ? "bg-brand-navy text-white" : "text-muted-foreground hover:text-foreground"}`}>
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? <div className="py-10"><LoadingState label="Loading calendar" /></div> : (
          <>
            {/* Month grid */}
            {view === "month" && (
              <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card" data-testid="cal-month">
                <div className="grid grid-cols-7 border-b border-border bg-secondary/50 text-center">
                  {WEEKDAYS.map((d) => <div key={d} className="py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{d}</div>)}
                </div>
                <div className="grid grid-cols-7">
                  {Array.from({ length: 42 }).map((_, i) => {
                    const day = addDays(startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1)), i);
                    const inMonth = day.getMonth() === anchor.getMonth();
                    const dayEvents = eventsForDay(day);
                    return (
                      <div key={i} onClick={() => { setAnchor(startOfDay(day)); setView("day"); }} data-testid={`cal-day-${day.toISOString().slice(0,10)}`}
                        className={`min-h-[92px] cursor-pointer border-b border-r border-border p-1.5 transition-colors hover:bg-secondary/40 ${inMonth ? "" : "bg-secondary/20 text-muted-foreground"}`}>
                        <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${isToday(day) ? "bg-brand-gold text-brand-navy" : ""}`}>{day.getDate()}</div>
                        <div className="space-y-0.5">
                          {dayEvents.slice(0, 3).map((e) => <EventChip key={e.id} e={e} />)}
                          {dayEvents.length > 3 && <p className="px-1 text-[10px] text-muted-foreground">+{dayEvents.length - 3} more</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Week */}
            {view === "week" && (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-7" data-testid="cal-week">
                {Array.from({ length: 7 }).map((_, i) => {
                  const day = addDays(startOfWeek(anchor), i);
                  const dayEvents = eventsForDay(day);
                  return (
                    <div key={i} className="rounded-xl border border-border bg-card p-3 shadow-card">
                      <div className={`mb-2 text-sm font-semibold ${isToday(day) ? "text-accent" : "text-foreground"}`}>{day.toLocaleDateString(undefined, { weekday: "short", day: "numeric" })}</div>
                      <div className="space-y-1">
                        {dayEvents.length === 0 ? <p className="text-xs text-muted-foreground">—</p> : dayEvents.map((e) => <EventChip key={e.id} e={e} />)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Day */}
            {view === "day" && (
              <div className="rounded-xl border border-border bg-card p-4 shadow-card" data-testid="cal-day-view">
                {eventsForDay(anchor).length === 0 ? (
                  <EmptyState title="Nothing scheduled" description="No events on this day." />
                ) : (
                  <div className="space-y-2">
                    {eventsForDay(anchor).map((e) => (
                      <button key={e.id} onClick={() => setSelected(e)} data-testid={`cal-event-${e.id}`}
                        className="flex w-full items-center gap-3 rounded-lg border border-border px-4 py-3 text-left hover:bg-secondary">
                        <div className="w-20 shrink-0 text-sm font-medium text-muted-foreground">{e.all_day ? "All day" : fmtTime(e.start_at)}</div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-foreground">{e.title}</p>
                          <p className="text-xs text-muted-foreground">{eventTypeLabel(e.event_type)}{e.location ? ` · ${e.location}` : ""}</p>
                        </div>
                        {e.source === "system" && <Badge variant="outline" className="gap-1 text-[10px]"><Lock className="h-2.5 w-2.5" /> Auto</Badge>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Agenda */}
            {view === "agenda" && (
              events.length === 0 ? (
                <EmptyState title="Your schedule starts here." description="Add appointments, showings, closings, and business events." />
              ) : (
                <div className="space-y-4" data-testid="cal-agenda">
                  {Object.entries(events.reduce((acc, e) => { const k = new Date(e.start_at).toDateString(); (acc[k] ||= []).push(e); return acc; }, {}))
                    .map(([dayKey, dayEvents]) => (
                      <div key={dayKey}>
                        <p className="mb-2 text-sm font-semibold text-foreground">{fmtWeekday(dayKey)}</p>
                        <div className="space-y-2">
                          {dayEvents.map((e) => (
                            <button key={e.id} onClick={() => setSelected(e)} data-testid={`cal-event-${e.id}`}
                              className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left shadow-card hover:bg-secondary">
                              <div className="w-20 shrink-0 text-sm font-medium text-muted-foreground">{e.all_day ? "All day" : fmtTime(e.start_at)}</div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-foreground">{e.title}</p>
                                <p className="text-xs text-muted-foreground">{eventTypeLabel(e.event_type)}{e.location ? ` · ${e.location}` : ""}</p>
                              </div>
                              {e.source === "system" && <Badge variant="outline" className="gap-1 text-[10px]"><Lock className="h-2.5 w-2.5" /> Auto</Badge>}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              )
            )}
          </>
        )}
      </div>

      {/* Event detail */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-md" data-testid="cal-event-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{selected.title}</DialogTitle>
                <DialogDescription>{eventTypeLabel(selected.event_type)}{selected.source === "system" ? " · Auto-generated" : ""}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <p className="text-muted-foreground">{selected.all_day ? fmtDate(selected.start_at) : fmtDateTime(selected.start_at)}{selected.end_at ? ` → ${fmtDateTime(selected.end_at)}` : ""}</p>
                {selected.location && <p className="flex items-center gap-1.5 text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {selected.location}</p>}
                {selected.description && <p className="whitespace-pre-wrap text-foreground">{selected.description}</p>}
                {selected.related?.label && (
                  <button className="flex items-center gap-1.5 text-accent hover:underline" data-testid="cal-event-related"
                    onClick={() => { const r = selected.related; setSelected(null); navigate(r.type === "transaction" ? `/transactions/${r.id}` : r.contact_id ? `/crm/${r.contact_id}` : "/calendar"); }}>
                    {selected.related.label} <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                )}
                {selected.source === "system" && <p className="rounded-md bg-secondary/60 px-3 py-2 text-xs text-muted-foreground">This event is generated from a {selected.system_type === "followup" ? "follow-up" : "transaction"} and updates automatically.</p>}
              </div>
              {selected.source !== "system" && (
                <DialogFooter>
                  <Button variant="ghost" className="text-destructive" onClick={cancelSelected} data-testid="cal-event-cancel">Cancel event</Button>
                  <Button variant="outline" onClick={() => { setEditEvent(selected); setSelected(null); }} data-testid="cal-event-edit">Edit</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <NewEventDialog open={!!editEvent} onOpenChange={(v) => !v && setEditEvent(null)} event={editEvent} onSaved={() => { setEditEvent(null); load(); }} />
    </AppShell>
  );
}
